import { useEffect, useRef } from 'react';

import { cn } from 'cn';
import Image from 'next/image';
import { useTranslations } from 'use-intl';

import { MobileAppFrame } from '@/shared-mobile-app';

import ProjectChat from './project-response-chat';
import { PROJECT_CHAT_CLASSNAMES } from './project-response-chat.types';
import type { HandsFreeMainValue } from './project-usability-handsfree-content.types';
import ProjectUsabilityImageChoiceStage from './project-usability-image-choice-stage';
import ProjectUsabilityMobileAppDebug from './project-usability-mobile-app-debug';
import ProjectUsabilityResponseIframe from './project-usability-response-iframe';
import ProjectUsabilityResponseSubmitted from './project-usability-response-submitted';

export default function ProjectUsabilityHandsFreeMain({
  project,
  orgSettings,
  showSubmitted,
  contentUrl,
  mobileAppEmbedUrl,
  debugEnabled,
  isSessionOver,
  isImageChoiceActive,
  lastAssistantMessage,
  imageChoiceValue,
  onImageChoiceChange,
  onSubmitContent,
  isSubmitting,
  effectiveIsConversationOver,
  chatProps,
  insightLoading,
  messages,
  onChatSubmit,
  onConversationOver,
  conversationOverText,
  activityStore,
  compact,
  shouldFloatControls,
  isMobileAppPreloading,
  onMainScreenClick,
}: HandsFreeMainValue) {
  const tCommon = useTranslations('Common');

  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = mainRef.current;
    if (!el || !onMainScreenClick) return;
    el.addEventListener('click', onMainScreenClick);
    return () => el.removeEventListener('click', onMainScreenClick);
  }, [onMainScreenClick]);

  return (
    <>
      <div className='sr-only' aria-hidden='true'>
        <ProjectChat
          {...chatProps}
          insightLoading={insightLoading}
          messages={messages}
          onSubmit={onChatSubmit}
          isConversationOver={effectiveIsConversationOver}
          onConversationOver={onConversationOver}
          conversationOverText={conversationOverText}
          activityStore={activityStore}
          classes={{
            root: PROJECT_CHAT_CLASSNAMES.usability,
          }}
        />
      </div>

      {!compact && (
        <header className='flex h-12 shrink-0 items-center px-4 sm:px-6'>
          <div className='w-16 sm:w-24'>
            {orgSettings?.logoUrl ? (
              <Image
                src={orgSettings.logoUrl}
                alt={tCommon('logoAlt')}
                width={200}
                height={50}
                sizes='96px'
                className='h-6 w-auto object-contain'
                loading='eager'

                unoptimized
              />
            ) : null}
          </div>
          <h1
            className='mx-auto line-clamp-1 max-w-xs flex-1 text-center font-semibold text-white'
            title={project.publicTitle ?? undefined}
          >
            {project.publicTitle}
          </h1>
          <div className='w-16 sm:w-24' />
        </header>
      )}

      <main
        ref={mainRef}
        data-float={shouldFloatControls}
        className={cn(
          'relative min-h-0 flex-1 px-3 sm:px-4',
          'data-[float=true]:pb-[calc(env(safe-area-inset-bottom)+0.5rem)]'
        )}
        data-test='handsfree-main-screen'
      >
        {(() => {
          if (showSubmitted) {
            return (
              <div className='size-full overflow-hidden rounded-2xl bg-gray-100'>
                <ProjectUsabilityResponseSubmitted variant='handsfree' />
              </div>
            );
          }
          const imageChoiceOverlay =
            isImageChoiceActive && lastAssistantMessage ? (
              <ProjectUsabilityImageChoiceStage
                key={lastAssistantMessage.id}
                message={lastAssistantMessage}
                onSubmit={(content) => onSubmitContent(content)}
                isSubmitting={isSubmitting}
                isConversationOver={effectiveIsConversationOver}
                showSendButton={false}
                value={imageChoiceValue}
                onChange={onImageChoiceChange}
                className='absolute inset-0 z-20 rounded-2xl'
              />
            ) : null;

          const mobileAppIframe = mobileAppEmbedUrl ? (
            <MobileAppFrame
              embedUrl={mobileAppEmbedUrl}
              language={project.language}
              containerClassName='h-full w-full rounded-2xl bg-gray-100'
              isConversationOver={isSessionOver}
              debugEnabled={debugEnabled}
              isHidden={isMobileAppPreloading}
              renderDebugPanel={(debugPanelProps) => (
                <ProjectUsabilityMobileAppDebug {...debugPanelProps} />
              )}
            >
              {!isMobileAppPreloading ? imageChoiceOverlay : null}
            </MobileAppFrame>
          ) : null;

          if (mobileAppEmbedUrl && !isMobileAppPreloading) {
            return mobileAppIframe;
          }

          return (
            <>
              {mobileAppIframe}
              <ProjectUsabilityResponseIframe
                contentUrl={contentUrl}
                containerClassName='h-full w-full rounded-2xl bg-gray-100'
                isConversationOver={isSessionOver}
              >
                {imageChoiceOverlay}
              </ProjectUsabilityResponseIframe>
            </>
          );
        })()}
      </main>
    </>
  );
}
